import type { PracticeSubmission } from "@huosu/shared";

type StoragePort = Pick<Storage, "getItem" | "setItem">;
/** Account-scoped, durable retry queue. No tokens are stored in the queue. */
export class PracticeOutbox {
  private running = false;
  constructor(private storage: StoragePort, private currentUser: () => string | undefined,
    private send: (payload: PracticeSubmission) => Promise<unknown>,
    private notify: (message: string, result?: unknown) => void) {}
  private key(user: string) { return `huosu_practice_outbox_v1:${user}`; }
  private read(user: string): PracticeSubmission[] {
    const raw = this.storage.getItem(this.key(user));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.some((item) => !item?.attemptId || !item?.sentenceId)) throw new Error("待同步记录读取失败，请保留浏览器数据并联系支持。");
    return parsed;
  }
  enqueue(payload: PracticeSubmission) {
    const user = this.currentUser();
    if (!user) return false;
    const queue = this.read(user);
    if (!queue.some((item) => item.attemptId === payload.attemptId)) queue.push({ ...payload, ownerUserId: user });
    this.storage.setItem(this.key(user), JSON.stringify(queue));
    this.notify(`有 ${queue.length} 条学习记录待同步`);
    void this.flush();
    return true;
  }
  async flush() {
    const user = this.currentUser();
    if (!user || this.running) return;
    this.running = true;
    try {
      const queue = this.read(user);
      for (const item of queue) {
        if (this.currentUser() !== user) return;
        const result = await this.send(item);
        if (this.currentUser() !== user) return;
        // Re-read so records queued while the request was in flight survive.
        const remaining = this.read(user).filter((entry) => entry.attemptId !== item.attemptId);
        this.storage.setItem(this.key(user), JSON.stringify(remaining));
        this.notify(remaining.length ? `有 ${remaining.length} 条学习记录待同步` : "", result);
      }
    } catch (error) {
      if (this.currentUser() === user) this.notify("学习记录尚未同步，已保留在本机。请检查网络或重新登录后重试。");
    } finally { this.running = false; }
  }
}
